import { isNil, propOr, type } from "ramda";
import { DeepPartial } from "redux";
import { PreferredLocale } from "../../utils/locales";
import { IPageLockSettings } from "../pages/CourseAuthor/CourseInstruction/Parts/PageLockSettings";

export interface IAssessment {
  earlyExit?: boolean;
  accumulateAnswers: boolean;
  questionDrawingPreference: QuestionDrawingPreference;
  isRandomized: boolean;
  limitAllowedMistakes: boolean;
  numAllowedMistakes: number;
  numCorrectAnswersRequired: number;
  questionBank: ICourseSet<IQuestionPart>;
}

export type CourseView<T, U extends object> = (obj: Nullable<U>) => Nullable<T>;
export type CourseUnitView = CourseView<ICourseUnit, IFullCourse>;
export type CourseGridPartView = CourseView<IGridPart, IFullCourse>;

export interface ICourseEditOperations {
  addChoiceExerciseItem: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ICourseInstructionContentPart) => () => void;
  addClozeGap: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ICourseInstructionContentPart) => (count: number) => void;
  addCourseDescription: (description: string) => void;
  editDivider: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IDividerPart) => (value: IDividerSettings) => void;
  addImmersiveBackground: () => void;
  addPart: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (type: CourseComponentType, position: number, properties?: any) => void;
  copyGridParts: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (gridPart: IGridPart, childParts: ICourseInstructionContentPart[], position: number | undefined) => void;
  copyPart: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ICourseInstructionContentPart, position: number | undefined, properties?: any) => void;
  addTitle: (unitPath?: CourseUnitView[], courseComponent?: ICourseComponent) => (title: string) => void;
  editSpacer: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ISpacerPart) => (value: ISpacerSettings) => void;
  editImage: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IImagePart) => (value: IImageSettings) => void;
  editDocument: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: IDocumentPart) => (value: IDocumentSettings) => void;
  changeBackground: (
    path: CourseUnitView[],
    comp: ICourseInstruction
  ) => (part: ITeachingContent) => (imageId: string) => void;
  changeInstructionType: (path: CourseUnitView[], item: ICourseInstruction, type: CourseInstructionType) => void;
  cloneCourseIntoOrg: (organizationId: string) => void;
  createComponent: (path: CourseUnitView[], tag: CourseComponentTag, type?: CourseInstructionType) => () => void;
  createStyle: () => void;
  deleteComponent: (path: CourseUnitView[], comp: ICourseComponent) => () => void;
  deleteCourse: () => void;
  deleteElement: <T>(element: T, position: string) => void;
  deleteInstructionPart: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ICourseInstructionContentPart) => () => void;
  deleteExerciseItem: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part?: ICourseInstructionContentPart) => (choiceId: number) => () => void;
  deleteClozeGap: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part?: ICourseInstructionContentPart) => (gapId: number) => () => void;
  modifyOrganizationOwner: (newOrganizationId: string) => void;
  modifyClozeGapAnswers: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (
    part: ICourseInstructionContentPart
  ) => (
    gapId: number,
    answerId: number,
    oldAnswers: string[],
    newAnswers: string[],
    currentLocale: PreferredLocale
  ) => () => void;
  modifyClozeGapHint: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (
    part: ICourseInstructionContentPart
  ) => (gapId: number, hintId: number, oldHint: string, newHint: string, currentLocale: PreferredLocale) => () => void;
  editHTMLContent: (
    path: CourseUnitView[],
    comp: ICourseComponent
  ) => (part: ITeachingContent) => (content: string, currentLocale?: PreferredLocale) => void;
  editVoiceOverMedia: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction
  ) => (part: ICourseInstructionContentPart) => (mediaId: string) => void;
  editVoiceOverText: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction
  ) => (part: ITeachingContent) => (text: string) => void;
  editVoiceOverTime: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction
  ) => (part: ICourseInstructionContentPart) => (startAt: Nullable<number>, stopAt: Nullable<number>) => void;
  enableVoiceOverAudio: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction
  ) => (part: ICourseInstructionContentPart) => () => void;
  togglePseudoLesson: (unitPath: CourseUnitView[], instructionSlide: ICourseInstruction) => (checked: boolean) => void;
  insertElement: <T>(element: T, position: string) => void;
  modifyAssessmentAccumulate: (unitPath: CourseUnitView[], accumulate: boolean) => void;
  modifyAssessmentDrawingPreference: (unitPath: CourseUnitView[], preference: QuestionDrawingPreference) => void;
  modifyAssessmentLimitMistakes: (unitPath: CourseUnitView[], shouldLimit: boolean) => void;
  modifyAssessmentMistakesAllowed: (unitPath: CourseUnitView[], num: number) => void;
  modifyAssessmentNumCorrectRequired: (unitPath: CourseUnitView[], num: number) => void;
  modifyAssessmentRandomized: (unitPath: CourseUnitView[], random: boolean) => void;
  modifyAssessmentSettings: (unitPath: CourseUnitView[], settings: ICourseContentSettings) => void;
  modifyAssessmentShowCorrect: (unitPath: CourseUnitView[], showCorrect: boolean) => void;
  modifyChoiceExerciseItemCorrect: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part?: ICourseInstructionContentPart) => (choiceId: number) => () => void;
  modifyExerciseItemText: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (
    part?: ICourseInstructionContentPart
  ) => (choiceId: number) => (content: string, currentLocale?: PreferredLocale) => void;
  modifyExerciseItemHint: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (
    part?: ICourseInstructionContentPart
  ) => (choiceId: number) => (content: string, currentLocale?: PreferredLocale) => void;
  modifyChoiceExerciseWrapperContent: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (
    part: ICourseInstructionContentPart
  ) => (text: string, wrapperKey: ChoiceExerciseWrapping, currentLocale?: PreferredLocale) => void;
  modifyChoiceExerciseRandom: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part: ICourseInstructionContentPart) => () => void;
  modifyClozeTemplate: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction | IAssessment
  ) => (part?: ICourseInstructionContentPart) => (template: string, currentLocale: PreferredLocale) => void;
  modifyCourseDescription: (description: string) => void;
  modifyCourseLinearity: () => void;
  modifyCustomCompletedMessage: (message: string) => void;
  modifyMasterLocale: (locale: PreferredLocale) => void;
  modifyTitle: (path?: CourseUnitView[], comp?: ICourseComponent) => (title: string) => void;
  modifyUseCustomCompletedMessage: () => void;
  moveOrCopyComponent: (path: CourseUnitView[], comp: ICourseComponent, insertAt: number) => void;
  publishVersion: (versionId?: number) => void;
  substColumnPart: (
    path: CourseUnitView[],
    comp: ICourseInstruction
  ) => (
    part: IGridPart
  ) => (
    position: number,
    newColumnValue: {
      type: CourseComponentType;
      properties: DeepPartial<ICourseInstructionContentPart>;
    } | null
  ) => void;
  toggleLockCourse: () => void;
  unpublishVersion: (versionId?: number) => void;
  modifyPageLockSettings: (
    unitPath: CourseUnitView[],
    instructionSlide: ICourseInstruction
  ) => (settings: IPageLockSettings) => void;
}

export interface ICourseNavigationOperations {
  setDisplayedComponent: (comp: Slide | null) => () => void;
  setPath: (path: CourseUnitView[]) => void;
  truncatePath: (index: number) => void;
}

export enum DefaultNames {
  Course = "Untitled Course",
  Unit = "Untitled Unit",
  Introduction = "Untitled Introduction",
  Lesson = "Untitled Lesson",
  Appendix = "Untitled Appendix",
  Summary = "Untitled Summary",
}

export enum CourseComponentTag {
  Unit = "koala-uc-unit",
  Instruction = "koala-uc-instruction",
}

export interface IDisplayGroups {
  [CourseComponentTag.Unit]: ICourseUnit[];
  [CourseComponentTag.Instruction]: ICourseInstruction[];
}

export interface ICourseInstruction {
  tag: CourseComponentTag.Instruction;
  id: number;
  content: ICourseInstructionContent;
  deleted?: boolean;
}

export function isInstruction(obj: any): obj is ICourseInstruction {
  return propOr(null, "tag", obj) === CourseComponentTag.Instruction;
}

interface IBaseContent<T> {
  content: T;
  deleted?: boolean;
  id: number;
  sort: number;
  tag: CourseComponentType;
}

export type ICourseInstructionContentPart =
  | ITeachingContent
  | IQuestionPart
  | IGridPart
  | IDividerPart
  | ISpacerPart
  | IImagePart
  | IDocumentPart;
export type IExerciseContentType =
  | GridContentWith<NullablePartPosition>
  | IChoiceExerciseContent<false>
  | IChoiceExerciseContent<true>
  | IClozeContent
  | IDividerSettings
  | IImageSettings
  | IListWithOptionsContent
  | IListWithoutOptionsContent
  | ISpacerSettings
  | ITeachingInnerContent
  | IUnorderedListContent
  | object;

export type IQuestionContentType =
  | IChoiceExerciseContent<false>
  | IChoiceExerciseContent<true>
  | IClozeContent
  | IListWithOptionsContent
  | IListWithoutOptionsContent
  | IUnorderedListContent
  | object;

export interface IDividerPart extends IBaseContent<IDividerSettings> {
  tag: CourseComponentType.Divider;
}

export interface ISpacerPart extends IBaseContent<ISpacerSettings> {
  tag: CourseComponentType.Spacer;
}

export interface IImagePart extends IBaseContent<IImageSettings> {
  tag: CourseComponentType.Image;
}

export interface IDocumentPart extends IBaseContent<IDocumentSettings> {
  tag: CourseComponentType.Document;
}

export type IQuestionPart =
  | IMultipleChoiceExercise
  | ISingleChoiceExercise
  | IClozeExercise
  | IListWithOptionsExercise
  | IListWithoutOptionsExercise
  | IUnorderedListExercise
  | IImageMapExercise;

export interface BackgroundSettings {
  imageId: Nullable<string>;
}

export interface ITeachingInnerContent {
  markdown: ICourseSet<ICourseLocalizedText>;
  voiceOverText: ICourseSet<ICourseLocalizedText>;
  voiceOver: ICourseSet<ICourseVoiceOver>;
  background?: BackgroundSettings;
}

export type BackgroundAblePart = ITeachingContent | IGridPart;

export interface ITeachingContent extends IBaseContent<ITeachingInnerContent> {
  tag: CourseComponentType.Teaching;
}

export type ColumnType = "TwoColumns" | "ThreeColumns";

interface ISimpleContent<T> {
  content: T;
  tag: CourseComponentType;
}

export type SimpleContent = ISimpleContent<ICourseInstructionContentPart["content"]>;

export type NullablePartPosition = number | null;

interface IGridContent<CT, TG = ColumnType> {
  columns: { content: CT; tag: TG };
  layout: { content: CT; tag: TG };
  background?: Nullable<BackgroundSettings>;
}

export type GridContentWith<P> = IGridContent<[P, P], "TwoColumns"> | IGridContent<[P, P, P], "ThreeColumns">;

export interface IGridPart extends IBaseContent<GridContentWith<NullablePartPosition>> {
  tag: CourseComponentType.Grid;
}

export interface IChoiceExerciseItem {
  correct: boolean;
  hint: ICourseSet<ICourseLocalizedText>;
  id: number;
  text: ICourseSet<ICourseLocalizedText>;
  deleted?: boolean;
}

export interface ILocalizedChoiceExerciseItem {
  correct: boolean;
  hint: Nullable<ICourseLocalizedText>;
  id: number;
  text: Nullable<ICourseLocalizedText>;
  sort: number;
}

export type ChoiceExerciseWrapping = keyof ICourseExerciseWrapping;

export interface ICourseExerciseWrapping {
  rebuke: ICourseSet<ICourseLocalizedText>;
  reinforcement: ICourseSet<ICourseLocalizedText>;
  taskAfter: ICourseSet<ICourseLocalizedText>;
  taskBefore: ICourseSet<ICourseLocalizedText>;
  title: ICourseSet<ICourseLocalizedText>;
}

export type IChoiceExercise = IMultipleChoiceExercise | ISingleChoiceExercise;

export interface IChoiceExerciseContent<SingleChoice = boolean> {
  items: ICourseSet<IChoiceExerciseItem>;
  randomize: boolean;
  singleChoice: SingleChoice;
  wrapping: ICourseExerciseWrapping;
}

export interface ISingleChoiceExercise extends IBaseContent<IChoiceExerciseContent<true>> {
  tag: CourseComponentType.Choice;
}

export interface IMultipleChoiceExercise extends IBaseContent<IChoiceExerciseContent<false>> {
  tag: CourseComponentType.Choice;
}

interface IClozeContent {
  gaps: ICourseSet<IClozeGap>;
  template: ICourseSet<ICourseLocalizedText>;
  wrapping: ICourseExerciseWrapping;
}

export interface IClozeExercise extends IBaseContent<IClozeContent> {
  tag: CourseComponentType.Cloze;
}

export interface IClozeGap {
  answers: ICourseSet<IClozeGapLocalizedAnswer>;
  deleted?: boolean;
  hint: ICourseSet<ICourseLocalizedText>;
  id: number;
}

export interface IClozeGapLocalized {
  answers: IClozeGapLocalizedAnswer[];
  hint: ICourseLocalizedText[];
  id: number;
}

export interface IClozeGapLocalizedAnswer {
  locale: PreferredLocale;
  value: {
    value: string[];
  };
  id: number;
}

interface IListWithOptionsContent {
  hintStyle: "ByItem";
  items: ICourseSet<IListItem>;
  wrapping: ICourseExerciseWrapping;
}

export interface IListWithOptionsExercise extends IBaseContent<IListWithOptionsContent> {
  tag: CourseComponentType.ListWithOptions;
}

export interface IListItem {
  id: number;
  hint: ICourseSet<ICourseLocalizedText>;
  text: ICourseSet<ICourseLocalizedText>;
  deleted?: boolean;
  sort: number;
}

interface IListWithoutOptionsContent {
  hintStyle: "ByItem";
  items: ICourseSet<ILocalizedListItem>;
  wrapping: ICourseExerciseWrapping;
}

export interface IListWithoutOptionsExercise extends IBaseContent<IListWithoutOptionsContent> {
  tag: CourseComponentType.ListWithoutOptions;
}

interface IUnorderedListContent {
  items: ICourseSet<ILocalizedListItem>;
  wrapping: ICourseExerciseWrapping;
}

export interface IUnorderedListExercise extends IBaseContent<IUnorderedListContent> {
  tag: CourseComponentType.UnorderedList;
}

export interface ILocalizedListItem {
  answers: ICourseSet<ILocalizedListItemAnswer>;
  deleted?: boolean;
  hint: ICourseSet<ICourseLocalizedText>;
  sort: number;
  id: number;
}

export interface ILocalizedListItemAnswer {
  id: number;
  locale: PreferredLocale;
  value: {
    value: string[];
  };
}

export interface IImageMapExercise extends IBaseContent<object> {
  tag: CourseComponentType.ImageMap;
}

export enum CourseComponentType {
  Grid = "grid",
  Teaching = "teaching",
  Choice = "choice",
  Cloze = "cloze",
  ListWithOptions = "list-with-options",
  ListWithoutOptions = "list-without-options",
  UnorderedList = "unordered-list",
  ImageMap = "image-map",
  WrittenFeedback = "written-feedback",
  RankingScaleFeedback = "ranking-scale-feedback",
  ChoiceFeedback = "choice-feedback",
  MultipleAnswerFeedback = "multiple-answer-feedback",
  Divider = "divider",
  Spacer = "spacer",
  Image = "image",
  Document = "document",
}

export const isExerciseType = (componentType: CourseComponentType): boolean => {
  switch (componentType) {
    case CourseComponentType.Choice:
      return true;
    case CourseComponentType.ImageMap:
      return true;
    case CourseComponentType.Cloze:
      return true;
    case CourseComponentType.ListWithOptions:
      return true;
    case CourseComponentType.ListWithoutOptions:
      return true;
    case CourseComponentType.UnorderedList:
      return true;
    default:
      return false;
  }
};

export interface ICourseVoiceOver {
  id: number;
  locale: PreferredLocale;
  value: {
    enabled: boolean;
    startAt: Nullable<number>;
    mediaId: Nullable<string>;
    stopAt: Nullable<number>;
  };
}

export enum CourseInstructionType {
  Introduction = "koala-intro",
  Lesson = "koala-lesson",
  Summary = "koala-summary",
  Appendix = "koala-appendix",
}

export interface ICourseInstructionContent {
  parts: ICourseSet<ICourseInstructionContentPart>;
  name: ICourseSet<ICourseLocalizedText>;
  type: CourseInstructionType;
  pseudoLesson: boolean;
  pageLockSettings: IPageLockSettings | null;
}

export interface ICourseUnit {
  tag: CourseComponentTag.Unit;
  content: ICourseUnitContent;
  id: number;
  deleted?: boolean;
}

export function isUnit(obj: any): obj is ICourseUnit {
  return propOr(null, "tag", obj) === CourseComponentTag.Unit;
}

export type ICourseComponent = ICourseUnit | ICourseInstruction;

export type Slide = ICourseComponent | IAssessment;

export interface ICourseUnitContent {
  settings: ICourseContentSettings;
  test: IAssessment;
  children: ICourseSet<ICourseComponent>;
  title: ICourseSet<ICourseLocalizedText>;
}

export interface ICourseSet<T> {
  elems: T[];
  nextId: number;
}

export interface ICourseLocalized {
  value: any;
  locale: PreferredLocale;
  id: number;
}

export function isLocalized(obj: any): obj is ICourseLocalized {
  return !isNil(obj.value) && type(obj.locale) === "String";
}

export interface ICourseLocalizedText {
  locale: PreferredLocale;
  value: {
    text: string;
  };
  id: number;
}

export interface ISpacerPatch {
  value: ISpacerSettings;
  id: number;
}

export interface IDividerPatch {
  value: IDividerSettings;
  id: number;
}

export function isCourseLocalizedText(obj: any): obj is ICourseLocalizedText {
  return type(obj.locale) === "String" && obj.value && type(obj.value.text) === "String" && type(obj.id) === "Number";
}

export interface ISpacerSettings {
  settings: {
    height: number;
    color: string;
  };
}

export interface IImageSettings {
  settings: {
    altText: string;
    link: string;
    isOpenInNewWindow: boolean;
    fileImageId: Nullable<string>;
    align: "center" | "left" | "right";
  };
}

export interface IDividerSettings {
  settings: {
    marginTop: number;
    marginBottom: number;
    color: string;
    style: "solid" | "dotted" | "dashed";
    thickness: number;
  };
}

export enum DocBorderThicknessPreset {
  Small = 10,
  Medium = 20,
  Large = 40,
}

export enum DocMarginPreset {
  Small = 30,
  Medium = 60,
  Large = 120,
}

export interface IDocumentSettings {
  settings: {
    style: "border" | "no-border";
    margin: DocMarginPreset;
    hasBackground: boolean;
    backgroundColor: string;
    size: "small" | "medium" | "full-width";
    fileDocumentId: string;
    borderThickness: DocBorderThicknessPreset;
    borderColor: string;
    borderStyle: "solid" | "dotted" | "dashed";
    fileName: string;
    fileSize: number;
    backgroundImageId: string;
    backgroundImageName: string;
    backgroundImageSize: number;
  };
}

export interface ICourseSettings {
  useCustomCompletedMessage: boolean;
  linear: boolean;
  customCompletedMessage: ICourseSet<ICourseLocalizedText>;
}

export interface ICourseContentSettings {
  doPostAssessment: boolean;
  doPreAssessment: boolean;
  showCorrectAnswersAfterAssessment: boolean;
}

export interface ICourseLocaleSettings {
  supportedLocales: PreferredLocale[];
  masterLocale: PreferredLocale;
}

export interface ICourseMetadata {
  mLDescription: ICourseSet<ICourseLocalizedText>;
  icon: string;
  title: string;
  description: string;
  mLTitle: ICourseSet<ICourseLocalizedText>;
  tags: string[];
}

export interface ICoursePublication {
  id: number;
  deleted?: boolean;
  notes: string;
  revision: number;
}

export interface ICourseChange {
  value: {
    createdAt: string;
  };
}

export interface IFullCourse {
  versionId: number;
  state: IMultiLocaleCourse;
}

export function isFullCourse(obj: any): obj is IFullCourse {
  return obj && Number.isInteger(obj.versionId) && !isNil(obj.state);
}

export interface PercentageWide {
  percentage: number;
}

export const DefaultWide = "default-wide";
export const SaltWide = "salt-wide";
export const FullResponsiveWide = "full-responsive-wide";

export type CoursePlayer = typeof DefaultWide | typeof SaltWide | typeof FullResponsiveWide | PercentageWide;

export enum CourseContent {
  SingleLocale = "koala-content",
  MultiLocale = "multi-locale-koala-content",
}

export interface IMultiLocaleCourse {
  admins: string[];
  listedIn: string[];
  publications: ICourseSet<ICoursePublication>;
  locked: boolean;
  localeSettings: ICourseLocaleSettings;
  owner: string;
  readSpeaker: boolean;
  content: ICourseContent;
  deleted: boolean;
  meta: ICourseMetadata;
  coursePlayer: CoursePlayer;
  background: Nullable<string>;
  clonedFrom: Nullable<string>;
  style: Nullable<string>;
  contentType: CourseContentType;
}

export interface ICourseContent {
  version: number;
  type: CourseContent;
  value: {
    settings: ICourseSettings;
    root: {
      settings: ICourseContentSettings;
      children: ICourseSet<ICourseComponent>;
      test: IAssessment;
      title: ICourseSet<ICourseLocalizedText>;
    };
  };
}

export enum CourseContentType {
  MarkdownContent = "markdown",
  HtmlContent = "html",
}

export enum QuestionDrawingPreference {
  PreferNewQuestions = "new",
  PreferWrongQuestions = "wrong",
  PreferNewOrWrongQuestions = "new-or-wrong",
}

export interface ICourseAssessmentLocalizedAnswers {
  locale: string;
  value: {
    value: any;
  };
  id: number;
}

export function isCourseLocalizedAnswer(obj: any): obj is ICourseAssessmentLocalizedAnswers {
  return type(obj.locale) === "String" && obj.value && obj.value.value && type(obj.id) === "Number";
}

export function isAssessment(obj: any): obj is IAssessment {
  const assessment = obj as IAssessment;
  return (
    !isNil(obj) &&
    assessment.accumulateAnswers !== undefined &&
    assessment.isRandomized !== undefined &&
    assessment.limitAllowedMistakes !== undefined &&
    assessment.numAllowedMistakes !== undefined &&
    assessment.numCorrectAnswersRequired !== undefined
  );
}

export interface ICourseResolveDisplayResponse {
  canonicalAlias: {
    kind: string;
    name: string;
  };
  deleted: boolean;
  displayName: string;
  locked: boolean;
  owner: string;
}

export interface ICourseStyle {
  state: {
    assets: ICourseSet<any>;
    bodyFontFamilyId: Nullable<string>;
    course: string;
    deleted: boolean;
    displayName: string;
    headerFontFamilyId: Nullable<string>;
    lessCode: string;
    logo: Nullable<string>;
    logoImageId: Nullable<string>;
    monospaceFontFamilyId: Nullable<string>;
    owner: string;
  };
  versionId: number;
}

export interface IPublicationInfo {
  publicationTitle: string;
  publishedRevId: number;
  createdAt: string;
}
